/**
 * ホームの使い方ツアー (初回ログイン時の案内) の状態管理。
 *
 * - 「一度見たか」は端末の localStorage に従業員ごとに記録する (DB は使わない)。
 *   キーにバージョンを含めるので、ツアーの内容を大きく変えたときは
 *   HOME_TOUR_VERSION を上げれば全員にもう一度表示される。
 * - ヘッダーの「？」メニューから再表示を頼まれたときは、sessionStorage に依頼フラグを
 *   立ててからホームへ遷移し、ホーム側がフラグを消費してツアーを開始する
 *   (既にホームにいる場合は HOME_TOUR_START_EVENT で即時に知らせる)。
 *
 * ブラウザ API に直接触れず storage を引数で受け取るのは、単体テストで差し替えるため。
 */

export const HOME_TOUR_VERSION = 1;

/** 既にホームを表示しているときにツアー開始を知らせる window イベント名 */
export const HOME_TOUR_START_EVENT = "gc:home-tour:start";

const SEEN_VALUE = "done";
const REQUEST_KEY = "gc:homeTour:request";

export type TourStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** 「ツアーを見た」記録のキー (例: gc:homeTour:v1:1234) */
export function homeTourSeenKey(employeeId: number): string {
  return `gc:homeTour:v${HOME_TOUR_VERSION}:${employeeId}`;
}

/**
 * ツアーを自動で始めてよいか (= この従業員がまだ見ていないか)。
 * storage が使えない環境 (null / 例外) では false を返す。記録できない環境で
 * 毎回ツアーが出続けるより、「？」メニューから見てもらう方が害が小さいため。
 */
export function shouldAutoStartHomeTour(
  storage: TourStorage | null,
  employeeId: number,
): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(homeTourSeenKey(employeeId)) !== SEEN_VALUE;
  } catch {
    return false;
  }
}

/** ツアーを見た (最後まで進んだ / スキップした / 閉じた) と記録する */
export function markHomeTourSeen(
  storage: TourStorage | null,
  employeeId: number,
): void {
  if (!storage) return;
  try {
    storage.setItem(homeTourSeenKey(employeeId), SEEN_VALUE);
  } catch {
    // 保存できなくても、次回もう一度表示されるだけなので無視する
  }
}

/** 「？」メニューから再表示を頼む (ホームへ遷移する前に呼ぶ) */
export function requestHomeTour(storage: TourStorage | null): void {
  if (!storage) return;
  try {
    storage.setItem(REQUEST_KEY, "1");
  } catch {
    // 保存できない環境では HOME_TOUR_START_EVENT 経由の即時開始だけが効く
  }
}

/** 再表示の依頼があれば取り消したうえで true を返す (1 回の依頼で 1 回だけ開始する) */
export function consumeHomeTourRequest(storage: TourStorage | null): boolean {
  if (!storage) return false;
  try {
    const requested = storage.getItem(REQUEST_KEY) === "1";
    if (requested) storage.removeItem(REQUEST_KEY);
    return requested;
  } catch {
    return false;
  }
}

/**
 * ツアーが指し示す画面上の場所 (CSS セレクタ)。
 * data-tour 属性はホームの各コンポーネント側に付けている。ヘッダー (AppBar) と
 * 下部メニュー (BottomNav) は既存の要素・aria-label (= 表示ラベル) をそのまま使う。
 * ヘッダーは右側のまとまり (ポイント・チケット・アイコン) を指す。
 */
export const HOME_TOUR_TARGETS = {
  header: "header > div:last-child",
  earnings: '[data-tour="home-earnings"]',
  treasure: '[data-tour="home-treasure"]',
  character: '[data-tour="home-character"]',
  missions: '[data-tour="home-missions"]',
  navStamp: 'nav button[aria-label="打刻"]',
  navMembers: 'nav button[aria-label="メンバー"]',
  bottomNav: "nav",
  helpMenu: '[data-tour="help-menu"]',
} as const;

export type HomeTourTargetKey = keyof typeof HOME_TOUR_TARGETS;

/**
 * 画面に無い場所を指すステップを除く。
 * target が null のステップ (画面中央に出す挨拶など) は常に残す。
 * 宝箱のように条件付きで表示される部品があるため、開始時点の画面で判定する。
 */
export function pickAvailableSteps<T extends { target: string | null }>(
  steps: readonly T[],
  isPresent: (selector: string) => boolean,
): T[] {
  return steps.filter((step) => step.target === null || isPresent(step.target));
}
